import { atom, getDefaultStore } from 'jotai';

export type CreditAllowanceRefusal = {
  projectId: string;
  ghostText: string;
  nudgeNonce: number;
  /** Epoch ms when the 402 landed — GET snapshots older than this are stale. */
  forcedAt: number;
  /** Skip follow-up POSTs and force the 100% banner until GET remaining > 0. */
  holdExhausted: boolean;
};

export const creditAllowanceRefusalAtom = atom<CreditAllowanceRefusal | null>(null);

export function rememberCreditAllowanceRefusal({
  projectId,
  ghostText,
}: {
  projectId: string;
  ghostText: string;
}): void {
  if (!projectId) return;
  const store = getDefaultStore();
  const current = store.get(creditAllowanceRefusalAtom);
  store.set(creditAllowanceRefusalAtom, {
    projectId,
    ghostText: ghostText.trim(),
    nudgeNonce: current?.projectId === projectId ? current.nudgeNonce : 0,
    forcedAt: Date.now(),
    holdExhausted: true,
  });
}

export function nudgeCreditAllowanceRefusal(projectId: string): void {
  const store = getDefaultStore();
  const current = store.get(creditAllowanceRefusalAtom);
  if (!current || current.projectId !== projectId) return;
  store.set(creditAllowanceRefusalAtom, {
    ...current,
    nudgeNonce: current.nudgeNonce + 1,
  });
}

/** GET remaining > 0 after the 402 — keep the ghost, allow send again. */
export function relaxCreditAllowanceRefusalHold(): void {
  const store = getDefaultStore();
  const current = store.get(creditAllowanceRefusalAtom);
  if (!current?.holdExhausted) return;
  store.set(creditAllowanceRefusalAtom, { ...current, holdExhausted: false });
}

export function clearCreditAllowanceRefusal(projectId?: string): void {
  const store = getDefaultStore();
  const current = store.get(creditAllowanceRefusalAtom);
  if (!current) return;
  if (projectId && current.projectId !== projectId) return;
  store.set(creditAllowanceRefusalAtom, null);
}

export function shouldSkipCreditAllowanceFollowUpSend(
  projectId: string | undefined
): boolean {
  if (!projectId) return false;
  const refusal = getDefaultStore().get(creditAllowanceRefusalAtom);
  return refusal?.projectId === projectId && refusal.holdExhausted;
}

/** Test-only: drop in-memory 402 refusal between Jest cases. */
export function __resetCreditAllowanceRefusalForTests(): void {
  getDefaultStore().set(creditAllowanceRefusalAtom, null);
}
